/* TDT · Header */
if (!customElements.get('tdt-header')) {
  class TdtHeader extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.listenerOptions = { signal: this.events.signal };
      this.bar = this.querySelector('.tdt-header__bar');
      this.burger = this.querySelector('.tdt-header__burger');
      this.drawer = this.querySelector('.tdt-drawer');
      this.langToggle = this.querySelector('.tdt-lang__toggle[aria-controls]');
      this.langList = this.querySelector('.tdt-lang__list');

      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.initLanguage();
      this.initDrawer();
      this.initDropdownA11y();

      if (this.dataset.sticky === 'true') {
        this.threshold = this.offsetTop + this.offsetHeight;
        this.lastScrollY = window.scrollY;
        window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.events.signal });
        this.onScroll();
      }

      document.addEventListener('click', this.onDocClick, this.listenerOptions);
      document.addEventListener('keyup', this.onKeyUp, this.listenerOptions);
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || !this.drawer || this.drawer.hidden) return;
        const nodes = [...this.querySelectorAll('.tdt-header__burger, .tdt-lang__toggle, .tdt-lang__list a, .tdt-drawer a, .tdt-drawer summary')].filter(n => n.getClientRects().length);
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }, this.listenerOptions);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.parentObserver?.disconnect();
      this.parentObserver = null;
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keyup', this.onKeyUp);
      document.documentElement?.classList.remove('tdt-no-scroll');
    }

    /* ---------- Idioma ---------- */
    initLanguage() {
      if (!this.langToggle || !this.langList) return;
      const form = this.langToggle.closest('form');
      const input = form.querySelector('input[name="language_code"]');

      this.langToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleLang(this.langToggle.getAttribute('aria-expanded') !== 'true');
      }, this.listenerOptions);

      this.langList.querySelectorAll('[data-value]').forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          input.value = option.dataset.value;
          form.submit();
        }, this.listenerOptions);
      });
    }

    toggleLang(open) {
      if (!this.langToggle) return;
      this.langToggle.setAttribute('aria-expanded', String(open));
      this.langList.hidden = !open;
    }

    /* ---------- Drawer móvil ---------- */
    initDrawer() {
      if (!this.burger || !this.drawer) return;
      this.burger.addEventListener('click', () => {
        this.toggleDrawer(this.burger.getAttribute('aria-expanded') !== 'true');
        if (this.drawer.hidden) this.burger.focus();
      }, this.listenerOptions);
    }

    toggleDrawer(open) {
      if (!this.burger) return;
      this.burger.setAttribute('aria-expanded', String(open));
      this.drawer.hidden = !open;
      this.classList.toggle('is-drawer-open', open);
      document.documentElement.classList.toggle('tdt-no-scroll', open);
      if (open) {
        this.closeInline();
        this.toggleLang(false);
        this.drawer.querySelector('summary, a')?.focus();
      } else this.drawer.querySelectorAll('details[open]').forEach(d => d.open = false);
    }

    /* ---------- Dropdowns: aria-expanded ---------- */
    initDropdownA11y() {
      if (this.classList.contains('tdt-header--inline')) {
        this.dataset.inlineReady = 'true';
        this.inlineItems = [...this.querySelectorAll('.tdt-header__item.has-dropdown')];
        this.inlineItems.forEach((item) => {
          const button = item.querySelector('.tdt-header__submenu-toggle');
          const parent = item.querySelector('.tdt-header__link');
          const measure = () => item.style.setProperty('--tdt-parent-width', parent.getBoundingClientRect().width + 'px');
          measure();
          this.parentObserver ||= new ResizeObserver(() => this.inlineItems.forEach(i => i.style.setProperty('--tdt-parent-width', i.querySelector('.tdt-header__link').getBoundingClientRect().width + 'px')));
          this.parentObserver.observe(parent);
          const set = (open) => {
            if (open) {
              this.closeInline(item);
              item.querySelector('.tdt-header__dropdown').scrollLeft = 0;
            }
            item.classList.toggle('is-submenu-open', open);
            button.setAttribute('aria-expanded', String(open));
          };
          item.addEventListener('mouseenter', () => { if (matchMedia('(hover: hover) and (min-width: 1400px)').matches) set(true); }, this.listenerOptions);
          item.addEventListener('mouseleave', () => { if (!item.contains(document.activeElement)) set(false); }, this.listenerOptions);
          button.addEventListener('click', () => set(button.getAttribute('aria-expanded') !== 'true'), this.listenerOptions);
          item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) set(false); }, this.listenerOptions);
        });
        window.addEventListener('resize', () => {
          if (innerWidth < 1400) this.closeInline();
          else if (this.burger?.getAttribute('aria-expanded') === 'true') this.toggleDrawer(false);
        }, this.listenerOptions);
        return;
      }
      this.querySelectorAll('.tdt-header__item.has-dropdown').forEach((item) => {
        const link = item.querySelector('.tdt-header__link');
        const set = (v) => link.setAttribute('aria-expanded', String(v));
        item.addEventListener('mouseenter', () => set(true), this.listenerOptions);
        item.addEventListener('mouseleave', () => set(false), this.listenerOptions);
        item.addEventListener('focusin', () => set(true), this.listenerOptions);
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) set(false);
        }, this.listenerOptions);
      });
    }

    closeInline(except) {
      this.inlineItems?.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-submenu-open');
        item.querySelector('.tdt-header__submenu-toggle').setAttribute('aria-expanded', 'false');
      });
    }

    /* ---------- Sticky ---------- */
    onScroll() {
      const y = window.scrollY;
      const stuck = y > this.threshold;
      const down = y > this.lastScrollY;
      if (this.dataset.scrollBehavior === 'always') {
        this.classList.remove('is-scroll-hidden');
      } else if (Math.abs(y - this.lastScrollY) > 2) {
        this.classList.toggle('is-scroll-hidden', stuck && down);
      }
      this.lastScrollY = y;
      if (!stuck) this.classList.remove('is-scroll-hidden');
      if (stuck === this.classList.contains('is-stuck')) return;
      if (stuck && !this.classList.contains('tdt-header--overlay')) {
        this.style.setProperty('--tdt-h-placeholder', this.offsetHeight + 'px');
      }
      this.classList.toggle('is-stuck', stuck);
      if (!stuck) this.toggleLang(false);
    }

    /* ---------- Cerrar con clic fuera / Esc ---------- */
    onDocClick(e) {
      if (!e.target.closest('.tdt-header__item')) this.closeInline();
      if (this.langList && !this.langList.hidden && !e.target.closest('.tdt-lang')) {
        this.toggleLang(false);
      }
    }

    onKeyUp(e) {
      if (e.key !== 'Escape') return;
      const activeItem = this.inlineItems?.find(item => item.classList.contains('is-submenu-open') && item.contains(document.activeElement));
      this.closeInline();
      activeItem?.querySelector('.tdt-header__submenu-toggle')?.focus();
      this.toggleLang(false);
      if (this.burger && this.burger.getAttribute('aria-expanded') === 'true') {
        this.toggleDrawer(false);
        this.burger.focus();
      }
    }
  }

  customElements.define('tdt-header', TdtHeader);
}
